import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

export interface DocketEntry {
  id: string;
  entry_date: string | null;
  entry_type: string | null;
  description: string;
  source: string;
  external_ref: string | null;
  seen: boolean;
  created_at: string;
}

/** External court-record activity for a case (NC eCourts Portal alerts). */
export function useCaseDocket(caseId: string | undefined) {
  return useQuery<DocketEntry[], Error>({
    queryKey: ["case-docket", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await berserkr().rpc("fetch_case_docket", { p_case_id: caseId });
      if (error) throw new Error(error.message);
      return (data as DocketEntry[]) ?? [];
    },
  });
}

export function useMarkDocketSeen(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      const { error } = await berserkr().rpc("mark_case_docket_seen", { p_case_id: caseId });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["case-docket", caseId] }),
  });
}
